/* SpawnAnything — weather with gameplay effects (feature:weather).
   Pure, deterministic helpers shared by the client (resolve the choice into a spec for `setup`) and the
   sim worker (apply the spec): rain = mud (slower, heavy units even slower, sliding), wind drifts
   projectiles and spoils hitscan aim, thunderstorms strike tall / metal units, fog shortens the reach of
   ranged weapons, blizzards slow everyone down and cool morale.
   Only dmath (mulberry32, dsin, dcos) and exact IEEE operations: every client computes the same battle. */
import { mulberry32, dsin, dcos } from '../dmath.js';

/** Event id (range 19–21 belongs to this feature): lightning strike [x, ground y, z, radius, top y, unit, building]. */
export const EV_WX_BOLT = 19;

/** Gameplay profile per weather at full intensity (k = 1). */
export const WEATHER = {
  klar: { name: 'Klar', mud: 0, wind: 0, fog: 0, cold: 0, bolt: 0, desc: 'Keine Wirkung auf den Kampf.' },
  regen: { name: 'Regen', mud: 1, wind: 0.25, fog: 0.2, cold: 0, bolt: 0, desc: 'Matschiger Boden: alle werden langsamer, schwere Einheiten noch mehr, und alle rutschen.' },
  gewitter: { name: 'Gewitter', mud: 1, wind: 0.5, fog: 0.3, cold: 0, bolt: 1, desc: 'Regen und Blitze: Einschläge mit Flächenschaden, bevorzugt in hohe oder metallische Einheiten und Türme.' },
  sturm: { name: 'Sturm', mud: 0, wind: 1, fog: 0, cold: 0, bolt: 0, desc: 'Starker Wind verweht Raketen, Gummihühner und Granaten und macht Schüsse ungenauer.' },
  sandsturm: { name: 'Sandsturm', mud: 0, wind: 0.85, fog: 0.45, cold: 0, bolt: 0, desc: 'Wind und Sand: Geschosse verwehen, Fernkämpfer sehen und treffen schlechter.' },
  nebel: { name: 'Nebel', mud: 0, wind: 0, fog: 1, cold: 0, bolt: 0, desc: 'Dichter Nebel: Fernkämpfer und Türme sehen nicht weit und müssen näher heran.' },
  schnee: { name: 'Schneesturm', mud: 0.25, wind: 0.5, fog: 0.35, cold: 1, bolt: 0, desc: 'Tiefer Schnee bremst alle, Kälte drückt auf die Moral.' },
};
export const WEATHER_KEYS = Object.keys(WEATHER);

/** "Passend zur Welt": weather and intensity per visual style (unlisted styles stay clear). */
export const STYLE_WEATHER = {
  winter: ['schnee', 0.55],
  wueste: ['sandsturm', 0.7],
  nacht: ['nebel', 0.6],
  neon: ['nebel', 0.45],
  film: ['regen', 0.6],
  gold: ['sturm', 0.4],
};

const TAU = 6.283185307179586;
const MAX_WIND = 9;            // m/s at wind = 1, k = 1
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const num = (v, d) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/**
 * Turn the player's choice ('auto' = passend zur Welt, 'zufall', or a WEATHER key) into a plain spec for `setup`.
 * Same choice + style + seed => same weather. metal = [team 0, team 1] attracts lightning.
 */
export function resolveWeather(choice, styleKey, seed, metal) {
  const rnd = mulberry32(((seed >>> 0) ^ 0x57e47e12) >>> 0);
  let key = choice, k = 1;
  if (choice === 'auto') { const s = STYLE_WEATHER[styleKey]; key = s ? s[0] : 'klar'; k = s ? s[1] : 1; }
  else if (choice === 'zufall') { key = WEATHER_KEYS[(rnd() * WEATHER_KEYS.length) | 0]; k = 0.55 + rnd() * 0.45; }
  if (!WEATHER[key]) key = 'klar';
  const dir = rnd() * TAU;
  return { key, k: Math.round(k * 100) / 100, dir: Math.round(dir * 1000) / 1000, metal: [metal && metal[0] ? 1 : 0, metal && metal[1] ? 1 : 0] };
}

/** Validate a spec from the network / setup; null = no weather effects at all. */
export function sanitizeWeather(w) {
  if (!w || typeof w !== 'object' || !Object.prototype.hasOwnProperty.call(WEATHER, w.key) || w.key === 'klar') return null;
  const P = WEATHER[w.key], k = clamp(num(w.k, 1), 0, 1), dir = clamp(num(w.dir, 0), -TAU, TAU);
  const ws = P.wind * k * MAX_WIND;
  const m = Array.isArray(w.metal) ? w.metal : [];
  return {
    key: w.key, k, dir, P,
    wx: dsin(dir) * ws, wz: dcos(dir) * ws, ws,                  // wind (m/s, blowing towards +dir)
    mud: P.mud * k, fog: P.fog * k, cold: P.cold * k, windK: P.wind * k,
    grip: 1 - 0.55 * P.mud * k,                                   // velocity response: lower = sliding
    bolt: P.bolt > 0 && k > 0, bt: 3 + 2.5 * k,                   // seconds until the first strike
    metal: [m[0] ? 1 : 0, m[1] ? 1 : 0],
  };
}

/** Army stats under the weather (a copy; the sim reads speed, acc, range and bravery from it). */
export function weatherArmy(W, A) {
  const heavy = clamp((A.mass - 1.5) / 4.5, 0, 1);              // soldier 0 … tank / giant 1
  const speed = A.speed * (1 - W.mud * (0.14 + 0.18 * heavy)) * (1 - 0.2 * W.cold);
  const acc = A.acc * (1 - 0.2 * W.windK) * (1 - 0.15 * W.fog);
  const range = A.melee ? A.range : Math.max(A.range * (1 - 0.45 * W.fog), Math.min(A.range, 6));
  const bravery = A.bravery < 1.5 ? A.bravery - 0.18 * W.cold : A.bravery;
  return { ...A, speed, acc, range, bravery };
}

/** Fog also blinds building guns: copies with shorter gun range (setup data stays untouched). */
export function weatherBuildings(W, list) {
  if (!W || !(W.fog > 0) || !Array.isArray(list)) return list;
  const f = 1 - 0.4 * W.fog;
  return list.map(b => b && Array.isArray(b.lv) ? { ...b, lv: b.lv.map(L => L && Array.isArray(L.guns) ? { ...L, guns: L.guns.map(g => ({ ...g, range: g.range * f })) } : L) } : b);
}

/** Apply the spec of a setup message: returns the sim weather (or null) and swaps in weathered army stats. */
export function weatherSetup(msg, army) {
  const W = sanitizeWeather(msg.weather);
  if (!W) return null;
  for (let k = 0; k < army.length; k++) army[k] = weatherArmy(W, army[k]); // every unit group
  if (W.fog > 0 && msg.buildings) msg.buildings = weatherBuildings(W, msg.buildings);
  return W;
}

/** Wind drift of projectile p (arcing shots drift most, fast shells barely). */
export function windProj(W, p, PARC, PVX, PVZ, dt) {
  const f = (PARC[p] ? 0.3 : 0.2) * dt;
  PVX[p] += W.wx * f; PVZ[p] += W.wz * f;
}

/** Lightning timer: true when a bolt strikes this tick (next one in ~2.5–7.5 s, more often when stronger). */
export function boltDue(W, dt, rnd) {
  W.bt -= dt;
  if (W.bt > 0) return false;
  W.bt = (2.5 + rnd() * 5) / (0.5 + 0.5 * W.k);
  return true;
}

/**
 * Pick the strike point: 12 random samples per team plus a few buildings; tall, metal units and gun towers
 * score highest. S = { N, X, Z, GY, Y, ALIVE, TEAM, army, W, nB, BALIVE, BH, BY, BLD, bGuns }.
 * out = [x, ground y, z, top y, unit or -1, building or -1].
 */
export function pickBolt(W, rnd, S, out) {
  let bu = -1, bb = -1, bs = -1;
  for (let t = 0; t < 2; t++) {
    const A = S.army[t], n = A.end - A.start;
    if (n <= 0) continue;
    for (let s = 0; s < 12; s++) {
      const i = A.start + ((rnd() * n) | 0);
      if (!S.ALIVE[i]) continue;
      const sc = (S.unitOf ? S.unitOf(i).h : A.h) + (W.metal[t] ? 6 : 0) + rnd() * 3;
      if (sc > bs) { bs = sc; bu = i; }
    }
  }
  for (let s = 0; s < 6 && S.nB > 0; s++) {
    const b = (rnd() * S.nB) | 0;
    if (!S.BALIVE[b]) continue;
    const sc = S.BH[b] + (S.bGuns[b].length ? 6 : 0) + rnd() * 3;
    if (sc > bs) { bs = sc; bb = b; bu = -1; }
  }
  if (bu >= 0) {
    const A = S.unitOf ? S.unitOf(bu) : S.army[S.TEAM[bu]];
    out[0] = S.X[bu]; out[1] = S.GY[bu]; out[2] = S.Z[bu]; out[3] = S.GY[bu] + S.Y[bu] + A.h;
  } else if (bb >= 0) {
    out[0] = S.BLD[bb].x; out[1] = S.BY[bb]; out[2] = S.BLD[bb].z; out[3] = S.BY[bb] + S.BH[bb];
  } else {
    const L = S.W * 0.6;
    out[0] = (rnd() - 0.5) * 2 * L; out[2] = (rnd() - 0.5) * 2 * L; out[1] = 0; out[3] = 0;
  }
  out[4] = bu; out[5] = bb;
  return out;
}

/** Strike strength: area radius and damage, extra damage to the struck unit (share of its max HP) or building. */
export const BOLT = { r: 3.6, dmg: 110, knock: 5, hpShare: 0.2, bDmg: 320 };
